import React, { useState, useEffect } from "react";
import {
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete,
  Box,
  Stack,
  Paper,
  Button,
  useTheme,
  Tooltip,
} from "@mui/material";
import { format } from "date-fns";
import { fromUTC } from "../GeneralUtil/timezoneUtil";
import { ButtonControls } from "../Components/SlidingSidebar";
import axios from "axios";
import { useUser } from "../UserContext";
import SelectBA, { Ambassador } from "../Components/SelectBA";
import ProfileBA from "../Profiles/BA/ProfileBA";
import Help from "@mui/icons-material/Help";
import EventHeader from "../Components/EventHeader";

interface Product {
  id: number;
  product: string;
}

interface BookEventProps {
  selectedDate: string;
  selectedSlot: {
    slot_id: number;
    start_time: string;
    end_time: string;
  } | null;
  accountId: number;
  accountInfo: {
    name: string;
    address: string;
    avatar_url: string;
  };
  isBAAvailable: boolean;
  availableBAs: Array<Ambassador>;
  onSuccess: () => void;
  onClose: () => void;
}

const BookEvent: React.FC<BookEventProps> = ({
  selectedDate,
  selectedSlot,
  accountId,
  accountInfo,
  isBAAvailable,
  availableBAs,
  onSuccess,
  onClose,
}) => {
  const { currentUser, selectedBrandId } = useUser();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [eventName, setEventName] = useState("");
  const [eventDesc, setEventDesc] = useState("");
  const [withoutAmbassador, setWithoutAmbassador] = useState(false);
  const [baSelectionMethod, setBaSelectionMethod] = useState("Standard");
  const [selectedProducts, setSelectedProducts] = useState<Product[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [validationError, setValidationError] = useState("");
  const [selectedBA, setSelectedBA] = useState<{
    id: number;
    username: string;
  } | null>(null);
  const [selectedAmbassadors, setSelectedAmbassadors] = useState<Ambassador[]>(
    []
  );

  const theme = useTheme();

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(
        `${
          import.meta.env.VITE_API_URL
        }/brands/products?selectedBrandId=${selectedBrandId}`
      );
      setProducts(response.data);
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  const handleSubmit = async () => {
    setError("");
    setSuccess(false);
    setValidationError("");

    // Validation
    if (!eventName.trim()) {
      setValidationError("Event name is required");
      return;
    }
    if (!eventDesc.trim()) {
      setValidationError("Event description is required");
      return;
    }
    if (!selectedSlot) {
      setValidationError("Please select a time slot");
      return;
    }

    setLoading(true);

    try {
      if (!currentUser?.user_id) {
        throw new Error("User not authenticated");
      }

      const payload = {
        eventName,
        eventDesc,
        status:
          baSelectionMethod === "Book Ambassador Now" ? "Booked" : "Pooling",
        retailId: accountId,
        bookingDate: selectedDate,
        startTime: selectedSlot.start_time,
        endTime: selectedSlot.end_time,
        creatorUserId: currentUser.user_id,
        products: selectedProducts.map((p) => p.id),
        withAmbassador: !withoutAmbassador,
        baSelectionMethod,
        selectedAmbassadorIds:
          baSelectionMethod === "Book Ambassador Now"
            ? selectedAmbassadors.map((ba) => ba.user_id)
            : undefined,
      };

      await axios.post(
        `${import.meta.env.VITE_API_URL}/events/events`,
        payload
      );

      setSuccess(true);
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error("Error creating event:", error);
      setError(
        error.response?.data?.message ||
          "Failed to create event. Please try again."
      );
      if (error.response) {
        console.log("Error response data:", error.response.data);
        console.log("Error response status:", error.response.status);
      }
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (time: string) => {
    if (!time) return "";
    const localTime = fromUTC(time);
    return format(new Date(`2000-01-01T${localTime}`), "h:mm aa");
  };

  // Map availableBAs to match the expected Ambassador structure
  const mappedAmbassadors = availableBAs.map((ba) => ({
    user_id: ba.user_id,
    username: ba.username,
    first_name: ba.first_name,
    last_name: ba.last_name,
    avatar_url: ba.avatar_url,
    avg_overall: ba.avg_overall,
    // Add any other required fields that Details uses
  }));

  return (
    <Box sx={{ p: 2 }}>
      <Paper
        elevation={1}
        sx={{
          borderRadius: 1,
          backgroundColor: "background.paper",
          p: 2,
          flex: 1,
        }}
      >
        <Stack spacing={2}>
          <EventHeader
            avatarUrl={accountInfo.avatar_url}
            accountName={accountInfo.name}
            accountAddress={accountInfo.address}
            selectedDate={selectedDate}
            startTime={formatTime(selectedSlot?.start_time || "")}
            endTime={formatTime(selectedSlot?.end_time || "")}
          />

          <TextField
            fullWidth
            label="Event Name"
            value={eventName}
            onChange={(e) => setEventName(e.target.value)}
            size="small"
            required
          />

          <TextField
            fullWidth
            label="Event Description"
            value={eventDesc}
            onChange={(e) => setEventDesc(e.target.value)}
            size="small"
            required
            multiline
            rows={3}
          />

          <Autocomplete
            multiple
            options={products}
            value={selectedProducts}
            onChange={(_, newValue) => setSelectedProducts(newValue)}
            getOptionLabel={(option) => option.product}
            renderInput={(params) => (
              <TextField {...params} label="Select Products" size="small" />
            )}
          />

          <FormControl fullWidth size="small">
            <InputLabel>Ambassador Selection Method</InputLabel>
            <Select
              value={baSelectionMethod}
              onChange={(e) => {
                setBaSelectionMethod(e.target.value);
                setWithoutAmbassador(e.target.value === "WithoutAmbassador");
              }}
              label="Ambassador Selection Method"
            >
              <MenuItem value="Book Ambassador Now" disabled={!isBAAvailable}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  Book Now
                  <Typography
                    component="span"
                    variant="body2"
                    color="text.secondary"
                    sx={{ ml: 1 }}
                  >
                    (+ $10)
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value="Standard">
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  Standard Pool
                  <Typography
                    component="span"
                    variant="body2"
                    color="text.secondary"
                    sx={{ ml: 1 }}
                  >
                    (Free)
                  </Typography>
                  <Tooltip title="PROMORAVEN will pool available ambassadors on a first come first serve basis over the next 24 hours.  The following 24 hour window will offer you a chance to review and select an ambassador.  If one is not selected during this window, one will be autobooked for you.  Autobooking preferences can be customized in your settings.">
                    <Help
                      fontSize="small"
                      color="primary"
                      sx={{ marginLeft: 1, fontSize: "0.9rem" }}
                    />
                  </Tooltip>
                </Box>
              </MenuItem>
              <MenuItem value="Premium">
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  Premium Pool
                  <Typography
                    component="span"
                    variant="body2"
                    color="text.secondary"
                    sx={{ ml: 1 }}
                  >
                    (+ $10)
                  </Typography>
                  <Tooltip title="PROMORAVEN will exclusively offer this event to a pool of top performing ambassadors, ensuring a higher quality of talent.">
                    <Help
                      fontSize="small"
                      color="primary"
                      sx={{ marginLeft: 1, fontSize: "0.9rem" }}
                    />
                  </Tooltip>
                </Box>
              </MenuItem>
              <MenuItem value="WithoutAmbassador">
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  Book Without Ambassador{" "}
                  <Typography
                    component="span"
                    variant="body2"
                    color="text.secondary"
                    sx={{ ml: 1 }}
                  >
                    (+ $15)
                  </Typography>
                </Box>
              </MenuItem>
            </Select>
          </FormControl>

          {isBAAvailable && baSelectionMethod === "Book Ambassador Now" && (
            <Box>
              <SelectBA
                ambassadors={mappedAmbassadors}
                selectedAmbassadors={selectedAmbassadors}
                onAmbassadorChange={(newValue) => {
                  setSelectedAmbassadors(
                    Array.isArray(newValue) ? newValue : []
                  );
                }}
                onAmbassadorClick={(ambassador) =>
                  setSelectedBA({
                    id: ambassador.id,
                    username: ambassador.username,
                  })
                }
                multiple={true}
                label=""
                placeholder="+ Add Ambassador..."
              />
            </Box>
          )}

          {selectedBA && (
            <ProfileBA
              baId={selectedBA.id}
              username={selectedBA.username}
              isModal={true}
              isOpen={true}
              onClose={() => setSelectedBA(null)}
            />
          )}

          {validationError && (
            <Typography color="error" sx={{ mt: 2 }}>
              {validationError}
            </Typography>
          )}

          {error && (
            <Typography color="error" sx={{ mt: 2 }}>
              {error}
            </Typography>
          )}

          {success && (
            <Typography color="success.main" sx={{ mt: 2 }}>
              Event booked successfully!
            </Typography>
          )}
        </Stack>
      </Paper>

      <ButtonControls>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={loading}
          sx={{ fontSize: theme.typography.body2.fontSize }}
        >
          {loading ? "Booking..." : "Book Event"}
        </Button>
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{ fontSize: theme.typography.body2.fontSize }}
        >
          Close
        </Button>
      </ButtonControls>
    </Box>
  );
};

export default BookEvent;
